import { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { fetchData, deleteData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { useSessionValidation } from '../../../utils/sessionValidation';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Fade, Tooltip, TextField, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EnquiryModal from './EnquiryModal';
import Pagination from '../../common/Pagination';

function EnquiryIndex() {
    // Session validation
    useSessionValidation();

    const [data, setData] = useState([]);
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [mounted, setMounted] = useState(false);
    const [loading, setLoading] = useState(false);
    const { isAuthenticated, companyId, branchId, logout, token } = useAuth();

    // Modal states
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('create'); // 'create', 'edit', 'view'
    const [selectedItem, setSelectedItem] = useState(null);

    // Dropdown data states
    const [customers, setCustomers] = useState([]);
    const [items, setItems] = useState([]);

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // Search state
    const [searchTerm, setSearchTerm] = useState('');
    const [searchExpanded, setSearchExpanded] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    // Date filter states
    const [fromDate, setFromDate] = useState(() => {
        const now = new Date();
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
        return firstDay.toISOString().split('T')[0];
    });
    const [toDate, setToDate] = useState(() => {
        const now = new Date();
        return now.toISOString().split('T')[0];
    });

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        setMounted(true);
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
    }, [isAuthenticated, companyId]);

    useEffect(() => {
        if (loading || !companyId || !token) return;

        setLoading(true);
        Promise.all([
            fetchTableData(),
            fetchCustomerData(),
            fetchItemData()
        ]).finally(() => {
            setLoading(false);
        });
    }, [companyId, token]);

    useEffect(() => {
        if (companyId && token && !loading) {
            fetchTableData();
        }
    }, [fromDate, toDate]);

    const fetchTableData = async () => {
        try {
            const response = await fetchData('ProductEnquiryApi', `/EnquriyList/${companyId}/${branchId}/${fromDate}/${toDate}`, token);

            if (response && response.responseCode === '1111' && response.response_data) {
                setData(response.response_data);
            } else {
                setData([]);
            }
        } catch (error) {
            console.error('Error fetching Enquiry Details:', error);
            setData([]);
        }
    };

    const fetchCustomerData = async () => {
        try {
            const customerResponse = await fetchData('CustomerApi', `/${companyId}`, token);
            if (customerResponse && customerResponse.responseCode === '1111' && customerResponse.response_data) {
                setCustomers(customerResponse.response_data);
            } else {
                setCustomers([]);
            }
        } catch (error) {
            console.error('Error fetching customers:', error);
            setCustomers([]);
        }
    };

    const fetchItemData = async () => {
        try {
            const itemResponse = await fetchData('SaleApi', `/ItemStockListDetails/${companyId}/${branchId}`, token);
            if (itemResponse && itemResponse.responseCode === '1111' && itemResponse.response_data) {
                setItems(itemResponse.response_data);
            } else {
                setItems([]);
            }
        } catch (error) {
            console.error('Error fetching items:', error);
            setItems([]);
        }
    };

    const handleDelete = async () => {
        try {
            const response = await deleteData('ProductEnquiryApi', deleteItemId, token);
            if (response && response.responseCode === '1111') {
                fetchTableData();
            }
        } catch (error) {
            console.error('Error deleting Enquiry:', error);
        }
        setDeleteItemId(null);
        setConfirmDialogOpen(false);
    };

    const openConfirmDialog = (itemId) => {
        setDeleteItemId(itemId);
        setConfirmDialogOpen(true);
    };

    const closeConfirmDialog = () => {
        setDeleteItemId(null);
        setConfirmDialogOpen(false);
    };

    // Modal handlers
    const handleOpenModal = (mode, enquiry = null) => {
        setModalMode(mode);
        setSelectedItem(enquiry);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedItem(null);
    };

    const handleSaveEnquiry = () => {
        fetchTableData(); // Refresh data
    };

    // Search handler
    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
        setCurrentPage(1); // Reset to first page when searching
    };

    // Filter data based on search term
    const filteredData = data.filter((item) => {
        const searchLower = searchTerm.toLowerCase();
        return (
            String(item.BillNumber || '').toLowerCase().includes(searchLower) ||
            String(item.CustomerName || '').toLowerCase().includes(searchLower) ||
            String(item.ContactNumber || '').toLowerCase().includes(searchLower) ||
            String(item.Remarks || '').toLowerCase().includes(searchLower)
        );
    });

    // Pagination handlers
    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleItemsPerPageChange = (newItemsPerPage) => {
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1);
    };

    // Calculate pagination values
    const totalItems = filteredData.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedData = filteredData.slice(startIndex, endIndex);

    // Define table columns
    const columns = [
        { field: 'BillNumber', header: 'Bill Number' },
        { field: 'CustomerName', header: 'Customer' },
        { field: 'ContactNumber', header: 'Contact No' },
        { field: 'EnquiryDate', header: 'Enquiry Date' },
        { field: 'Remarks', header: 'Remarks' },
    ];

    // Define action buttons for each row
    const renderActions = (item) => (
        <div style={{ display: 'flex', gap: '2px', alignItems: 'center', justifyContent: 'flex-end' }}>
            <Tooltip title="View Details" arrow>
                <Button
                    size="small"
                    onClick={async () => {
                        try {
                            const response = await fetchData('ProductEnquiryApi', `/${item.Id}`, token);
                            if (response && response.responseCode === '1111' && response.response_data) {
                                handleOpenModal('view', response.response_data);
                            }
                        } catch (error) {
                            console.error('Error fetching enquiry details:', error);
                        }
                    }}
                    style={{
                        minWidth: '22px',
                        height: '22px',
                        borderRadius: '6px',
                        color: 'white',
                        padding: '2px',
                        boxShadow: "0 1px 4px rgba(13, 118, 110, 0.3)",
                        border: 'none',
                        fontSize: '10px',
                        fontWeight: '600'
                    }}
                >
                    👁️
                </Button>
            </Tooltip>

            <Tooltip title="Edit Enquiry" arrow>
                <Button
                    size="small"
                    onClick={async () => {
                        try {
                            const response = await fetchData('ProductEnquiryApi', `/${item.Id}`, token);
                            if (response && response.responseCode === '1111' && response.response_data) {
                                handleOpenModal('edit', response.response_data);
                            }
                        } catch (error) {
                            console.error('Error fetching enquiry details:', error);
                        }
                    }}
                    style={{
                        minWidth: '22px',
                        height: '22px',
                        borderRadius: '6px',
                        color: 'white',
                        padding: '2px',
                        boxShadow: '0 1px 4px rgba(17, 153, 142, 0.3)',
                        border: 'none',
                        fontSize: '10px',
                        fontWeight: '600'
                    }}
                >
                    ✏️
                </Button>
            </Tooltip>

            <Tooltip title="Send WhatsApp" arrow>
                <Button
                    size="small"
                    onClick={async () => {
                        try {
                            const response = await fetchData('ProductEnquiryApi', `/${item.Id}`, token);
                            const fullEnquiry = response.response_data;

                            if (!fullEnquiry) return;

                            let message = `*Product Enquiry*\n\n`;
                            message += `Bill No: ${fullEnquiry.ProductEnquiry.BillNumber}\n`;
                            message += `Customer: ${fullEnquiry.ProductEnquiry.CustomerName}\n`;
                            message += `Date: ${formatDate(fullEnquiry.ProductEnquiry.EnquiryDate)}\n\n`;
                            message += `*Items:*\n`;

                            if (fullEnquiry.ProductEnquiryDetails && fullEnquiry.ProductEnquiryDetails.length > 0) {
                                fullEnquiry.ProductEnquiryDetails.forEach((item, index) => {
                                    message += `\n${index + 1}. ${item.ItemName}\n`;
                                    message += `   Quantity: ${item.Quantity}\n`;
                                    message += `   Days: ${item.Days}\n`;
                                    message += `   Rate: ₹${parseFloat(item.Rate).toLocaleString('en-IN')}\n`;
                                    message += `   Amount: ₹${parseFloat(item.Amount).toLocaleString('en-IN')}\n`;
                                });

                                const totalAmount = fullEnquiry.ProductEnquiryDetails.reduce((sum, item) => sum + parseFloat(item.Amount || 0), 0);
                                message += `\n*Total Amount: ₹${totalAmount.toLocaleString('en-IN')}*`;
                            }

                            if (fullEnquiry.ProductEnquiry.Remarks) {
                                message += `\n\n*Remarks:* ${fullEnquiry.ProductEnquiry.Remarks}`;
                            }

                            const encodedMessage = encodeURIComponent(message);
                            const phoneNumber = fullEnquiry.ProductEnquiry.ContactNumber?.replace(/[^0-9]/g, '');
                            const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
                            window.open(whatsappUrl, '_blank');
                        } catch (error) {
                            console.error('Error sending WhatsApp message:', error);
                        }
                    }}
                    style={{
                        minWidth: '22px',
                        height: '22px',
                        borderRadius: '6px',
                        backgroundColor: '#25D366',
                        color: 'white',
                        padding: '2px',
                        boxShadow: '0 1px 4px rgba(37, 211, 102, 0.3)',
                        border: 'none',
                        fontSize: '14px',
                        fontWeight: '600'
                    }}
                >
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="white">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                </Button>
            </Tooltip>

            <Tooltip title="Delete Enquiry" arrow>
                <Button
                    size="small"
                    onClick={() => openConfirmDialog(item.Id)}
                    style={{
                        minWidth: '22px',
                        height: '22px',
                        borderRadius: '6px',
                        color: 'white',
                        padding: '2px',
                        boxShadow: '0 1px 4px rgba(255, 107, 107, 0.3)',
                        border: 'none',
                        fontSize: '10px',
                        fontWeight: '600'
                    }}
                >
                    🗑️
                </Button>
            </Tooltip>
        </div>
    );

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-GB');
    };

    return (
        <Fade in={mounted} timeout={800}>
            <div style={{
                minWidth: '100%',
                background: '#f5f7fa',
                padding: '10px',
                position: 'relative'
            }}>
                <style>{`
          @keyframes slideIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .action-button {
            text-transform: none;
            font-weight: 600;
            letter-spacing: 0.5px;
          }
        `}</style>

                <div className="suite-page-panel" style={{
                    maxWidth: '1400px',
                    margin: '0 auto',
                    background: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                    padding: '12px',
                    position: 'relative',
                    color: 'black',
                    fontFamily: "Segoe UI, sans-serif",
                }}>
                    {/* Header Section */}
                    <div className="suite-section-heading" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '10px',
                        paddingBottom: '8px',
                        borderBottom: '1px solid #f0f0f0'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div>
                                <h4 style={{
                                    fontSize: '17px',
                                    fontWeight: '700',
                                    margin: 0,
                                    letterSpacing: '-0.3px',
                                    fontFamily: "Segoe UI, sans-serif",
                                    textTransform: 'uppercase'
                                }}>
                                    <span style={{ color: 'black', fontFamily: "Segoe UI, sans-serif", }}>Product Enquiry</span>
                                </h4>
                            </div>
                        </div>
                    </div>

                    {/* Date Filters, Search and Create Button Row - Horizontally Scrollable */}
                    <div style={{
                        marginBottom: '12px',
                        overflowX: 'auto',
                        overflowY: 'hidden',
                        WebkitOverflowScrolling: 'touch',
                        scrollbarWidth: 'thin'
                    }}>
                        <div style={{
                            display: 'flex',
                            gap: '8px',
                            alignItems: 'flex-end',
                            minWidth: 'fit-content',
                            paddingBottom: '4px'
                        }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: isMobile ? '120px' : '140px' }}>
                                <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>From Date</label>
                                <TextField
                                    type="date"
                                    size="small"
                                    value={fromDate}
                                    onChange={(e) => setFromDate(e.target.value)}
                                    sx={{
                                        width: isMobile ? '120px' : '140px',
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '6px',
                                            backgroundColor: '#f8f9fa',
                                            fontSize: '11px',
                                            height: '32px',
                                            '&:hover fieldset': { borderColor: "#0d766e" },
                                            '&.Mui-focused fieldset': { borderColor: "#0d766e" },
                                        },
                                    }}
                                />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: isMobile ? '120px' : '140px' }}>
                                <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>To Date</label>
                                <TextField
                                    type="date"
                                    size="small"
                                    value={toDate}
                                    onChange={(e) => setToDate(e.target.value)}
                                    sx={{
                                        width: isMobile ? '120px' : '140px',
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '6px',
                                            backgroundColor: '#f8f9fa',
                                            fontSize: '11px',
                                            height: '32px',
                                            '&:hover fieldset': { borderColor: "#0d766e" },
                                            '&.Mui-focused fieldset': { borderColor: "#0d766e" },
                                        },
                                    }}
                                />
                            </div>

                            {/* Search - Icon on mobile, always visible on desktop */}
                            {isMobile ? (
                                searchExpanded ? (
                                    <TextField
                                        size="small"
                                        placeholder="Search..."
                                        value={searchTerm}
                                        onChange={handleSearchChange}
                                        autoFocus
                                        onBlur={() => {
                                            if (!searchTerm) {
                                                setSearchExpanded(false);
                                            }
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon style={{ color: "#0d766e", fontSize: '18px' }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            minWidth: '250px',
                                            width: '350px',
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '6px',
                                                backgroundColor: '#f8f9fa',
                                                fontSize: '12px',
                                                height: '32px',
                                                '&:hover fieldset': {
                                                    borderColor: "#0d766e",
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: "#0d766e",
                                                },
                                            },
                                        }}
                                    />
                                ) : (
                                    <Tooltip title="Search" arrow>
                                        <Button
                                            onClick={() => setSearchExpanded(true)}
                                            style={{
                                                minWidth: '32px',
                                                width: '32px',
                                                height: '32px',
                                                padding: '0',
                                                borderRadius: '6px',
                                                backgroundColor: '#f8f9fa',
                                                border: '1px solid #e2e8f0',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <SearchIcon style={{ color: "#0d766e", fontSize: '18px' }} />
                                        </Button>
                                    </Tooltip>
                                )
                            ) : (
                                <TextField
                                    size="small"
                                    placeholder="Search..."
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon style={{ color: "#0d766e", fontSize: '18px' }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        flex: 1,
                                        minWidth: '250px',
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '6px',
                                            backgroundColor: '#f8f9fa',
                                            fontSize: '12px',
                                            height: '32px',
                                            '&:hover fieldset': {
                                                borderColor: "#0d766e",
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: "#0d766e",
                                            },
                                        },
                                    }}
                                />
                            )}

                            <Tooltip title="Create a new enquiry" arrow>
                                <Button
                                    variant="contained"
                                    onClick={() => handleOpenModal('create')}
                                    className="action-button"
                                    style={{
                                        background: "linear-gradient(135deg, #0d766e 0%, #164e63 100%)",
                                        color: 'white',
                                        padding: isMobile ? '0' : '4px 12px',
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        fontWeight: '700',
                                        boxShadow: "0 2px 8px rgba(13, 118, 110, 0.4)",
                                        border: 'none',
                                        height: '32px',
                                        textTransform: 'none',
                                        letterSpacing: '0.5px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '4px',
                                        whiteSpace: 'nowrap',
                                        minWidth: isMobile ? '32px' : '100px',
                                        width: 'auto',
                                        marginLeft: 'auto'
                                    }}
                                >
                                    <span style={{ fontSize: '18px' }}>+</span>
                                    {!isMobile && <span style={{ fontSize: '12px' }}>Create</span>}
                                </Button>
                            </Tooltip>
                        </div>
                    </div>

                    {/* Table */}
                    <div style={{ marginBottom: '10px' }}>
                        <TableContainer component={Paper} style={{
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                            borderRadius: '10px',
                            overflow: 'hidden'
                        }}>
                            <Table sx={{ minWidth: 650 }} aria-label="enquiry table">
                                <TableHead >
                                    <TableRow style={{
                                        background: "linear-gradient(135deg, #0d766e 0%, #164e63 100%)", height: '28px'
                                    }}>
                                        {columns.map((column) => (
                                            <TableCell key={column.field} style={{
                                                color: 'white',
                                                fontWeight: '600',
                                                fontSize: '10px',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px',
                                                padding: '4px 8px',
                                                height: '28px'
                                            }}>
                                                {column.header}
                                            </TableCell>
                                        ))}
                                        <TableCell style={{
                                            color: 'white',
                                            fontWeight: '600',
                                            fontSize: '10px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            padding: '4px 8px',
                                            textAlign: 'right',
                                            height: '28px'
                                        }}>
                                            Actions
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginatedData.map((item, index) => (
                                        <TableRow key={item.Id} style={{
                                            background: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                                            borderBottom: '2px solid #f0f0f0'
                                        }}>
                                            <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                                                {item.BillNumber}
                                            </TableCell>
                                            <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                                                {item.CustomerName}
                                            </TableCell>
                                            <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                                                {item.ContactNumber}
                                            </TableCell>
                                            <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                                                {formatDate(item.EnquiryDate)}
                                            </TableCell>
                                            <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {item.Remarks || '-'}
                                            </TableCell>
                                            <TableCell style={{ padding: '8px 12px', textAlign: 'right' }}>
                                                {renderActions(item)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </div>

                    {/* Pagination */}
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalItems={totalItems}
                        itemsPerPage={itemsPerPage}
                        onPageChange={handlePageChange}
                        onItemsPerPageChange={handleItemsPerPageChange}
                        pageSizeOptions={[10, 25, 50, 100]}
                    />
                </div>

                {/* Delete Confirmation Dialog */}
                <Dialog
                    open={confirmDialogOpen}
                    onClose={closeConfirmDialog}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                    PaperProps={{
                        style: {
                            borderRadius: '12px',
                            padding: '8px'
                        }
                    }}
                >
                    <DialogTitle id="alert-dialog-title" style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        color: '#2d3748'
                    }}>
                        Delete Record?
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText id="alert-dialog-description" style={{
                            fontSize: '13px',
                            color: '#666'
                        }}>
                            Are you sure you want to delete this enquiry record?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions style={{ padding: '8px 12px', gap: '6px' }}>
                        <Button
                            onClick={closeConfirmDialog}
                            style={{
                                background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                                color: '#ffffff',
                                borderRadius: '6px',
                                padding: '4px 8px',
                                fontSize: '12px',
                                fontWeight: '600',
                                textTransform: 'none',
                                boxShadow: '0 1px 4px rgba(255, 107, 107, 0.3)',
                                border: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                minHeight: '26px'
                            }}
                        >
                            <span style={{ fontSize: '12px' }}>❌</span>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleDelete}
                            style={{
                                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                                color: '#ffffff',
                                borderRadius: '6px',
                                padding: '4px 8px',
                                fontSize: '12px',
                                fontWeight: '600',
                                textTransform: 'none',
                                boxShadow: '0 1px 4px rgba(17, 153, 142, 0.3)',
                                border: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                minHeight: '26px'
                            }}
                        >
                            <span style={{ fontSize: '12px' }}>✅</span>
                            Confirm Delete
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Enquiry Modal */}
                <EnquiryModal
                    open={modalOpen}
                    onClose={handleCloseModal}
                    onSave={handleSaveEnquiry}
                    enquiryData={selectedItem}
                    mode={modalMode}
                    customers={customers}
                    items={items}
                />

                {/* Footer */}
                <div style={{
                    position: 'fixed',
                    bottom: '20px',
                    right: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 20px',
                    background: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '16px',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                    fontFamily: 'serif',
                    fontWeight: '600',
                    color: '#2c3e50',
                    fontSize: '13px',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    zIndex: 1000
                }}>
                    <span style={{ fontSize: '18px' }}>©️</span>
                    <span>EFFTABS SOLUTIONS LLP</span>
                </div>
            </div>
        </Fade>
    );
}

export default EnquiryIndex;
